import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Box, Button, Theme, useMediaQuery } from "@mui/material";
import { ActionIconButton, ResponsiveDropdown } from "@components";
import { ConfirmDeleteDialog } from "@components";
import { useDeleteInvestment } from "@rentablo/api/apiv1";
import AddIcon from "@icons/myicons/plus.svg?react";
import DotsIcon from "@icons/myicons/dots.svg?react";
import { useInvestmentMenuItems } from "./useInvestmentMenuItems";

interface Props {
  investmentId: number;
  investmentName?: string;
}

export const InvestmentPageActions = ({
  investmentId,
  investmentName,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: deleteInvestment } = useDeleteInvestment({
    throwOnError: true,
  });

  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const menuItems = useInvestmentMenuItems({
    investmentId,
    handleOpenDeleteModal: () => setDeleteModalShow(true),
  });

  const handleDeleteInvestment = async () => {
    await deleteInvestment({ id: investmentId });
    navigate("/investments");
  };

  const primaryLabel = t("investment.header.actions.addBookingButton");

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        {!isSm && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon width="18px" height="12px" />}
            onClick={() => navigate(`/add-transaction-wizard/${investmentId}`)}
          >
            {primaryLabel}
          </Button>
        )}
        <ResponsiveDropdown
          id="investment-actions-menu"
          mobileTitle={investmentName}
          items={menuItems}
          anchor={
            <ActionIconButton
              data-testid="open-menu-button"
              color="tertiary"
              size="small"
            >
              <DotsIcon />
            </ActionIconButton>
          }
        />
      </Box>
      <ConfirmDeleteDialog
        isOpen={deleteModalShow}
        title={t("investment.header.deleteModal.question")}
        onClose={() => setDeleteModalShow(false)}
        onDelete={handleDeleteInvestment}
      />
    </>
  );
};
